import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Fab,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Container,
  Fade
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, db } from '../config/firebase';
import { Documento } from '../types';
import DocumentosGaleria from '../components/documentos/DocumentosGaleria';
import SubirDocumentoForm from '../components/documentos/SubirDocumentoForm';
import { useAuth } from '../context/AuthContext';
import { useObras } from '../context/ObrasContext';
import { Layout } from '../components/layout/Layout';
import { usePermisosContext } from '../context/PermisosContext';

const DocumentosPage = () => {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const { obras, obraSeleccionada } = useObras();
  const [busqueda, setBusqueda] = useState('');
  const [openSubirDocumento, setOpenSubirDocumento] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { usuario } = useAuth();
  const { verificarPermisosObra, puedeEditar } = usePermisosContext();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!usuario) return;
    cargarDocumentos();
  }, [obraSeleccionada, busqueda, usuario]);

  const cargarDocumentos = async () => {
    setLoadingDocumentos(true);
    try {
      const documentosRef = collection(db, 'documentos');
      let documentosData: Documento[] = [];

      const isRestricted = usuario && usuario.role?.toUpperCase() !== 'ADMIN' && !usuario.puedeVerTodasLasObras;

      if (obraSeleccionada) {
        // Caso 1: Obra específica seleccionada
        if (isRestricted && !usuario?.obrasAsignadas?.includes(obraSeleccionada)) {
          setDocumentos([]);
          return;
        }

        const q = query(documentosRef, where('obra', '==', obraSeleccionada));
        const documentosSnap = await getDocs(q);
        documentosData = documentosSnap.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            obraId: data.obra || data.obraId,
            fechaSubida: data.fechaSubida?.toDate ? data.fechaSubida.toDate() : new Date(data.fechaSubida),
          } as Documento;
        });

      } else {
        // Caso 2: "Todas las Obras"
        if (isRestricted) {
          const assignedObras = usuario?.obrasAsignadas || [];

          if (assignedObras.length === 0) {
            setDocumentos([]);
            return;
          }

          const chunks = [];
          for (let i = 0; i < assignedObras.length; i += 10) {
            chunks.push(assignedObras.slice(i, i + 10));
          }

          const promises = chunks.map(chunk => {
            const q = query(documentosRef, where('obra', 'in', chunk));
            return getDocs(q);
          });

          const snapshots = await Promise.all(promises);

          snapshots.forEach(snap => {
            const docs = snap.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                obraId: data.obra || data.obraId,
                fechaSubida: data.fechaSubida?.toDate ? data.fechaSubida.toDate() : new Date(data.fechaSubida),
              } as Documento;
            });
            documentosData = [...documentosData, ...docs];
          });

        } else {
          const q = query(documentosRef);
          const documentosSnap = await getDocs(q);
          documentosData = documentosSnap.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              obraId: data.obra || data.obraId,
              fechaSubida: data.fechaSubida?.toDate ? data.fechaSubida.toDate() : new Date(data.fechaSubida),
            } as Documento;
          });
        }
      }

      documentosData = documentosData.sort((a, b) => b.fechaSubida.getTime() - a.fechaSubida.getTime());

      if (busqueda) {
        const busquedaLower = busqueda.toLowerCase();
        documentosData = documentosData.filter(doc => {
          return !busqueda || (
            (doc.nombre && doc.nombre.toLowerCase().includes(busquedaLower)) ||
            (doc.descripcion && doc.descripcion.toLowerCase().includes(busquedaLower)) ||
            (doc.usuarioEmail && doc.usuarioEmail.toLowerCase().includes(busquedaLower))
          );
        });
      }

      setDocumentos(documentosData);
    } catch (error) {
      console.error(error);
      setError('Error al cargar los documentos. Por favor, intente nuevamente.');
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const handleSubirDocumento = async ({
    file,
    nombre,
    obraId: selectedObraId
  }: {
    file: File;
    nombre: string;
    obraId: string;
  }) => {
    setError(null);
    setLoading(true);
    try {
      if (!puedeEditar()) {
        throw new Error('No tienes permisos para subir documentos');
      }
      if (!verificarPermisosObra(selectedObraId)) {
        throw new Error('No tienes acceso a esta obra');
      }

      // Validar tamaño del archivo (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        throw new Error('El archivo es demasiado grande. El tamaño máximo es 20MB.');
      }

      // Sanitize filename to avoid issues with special characters in Storage Rules
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const storageRef = ref(storage, `obras/${selectedObraId}/documentos/${Date.now()}_${sanitizedFileName}`);

      const metadata = {
        customMetadata: {
          obraId: selectedObraId,
          uploadedBy: usuario?.uid || '',
          role: usuario?.role || '',
          email: usuario?.email || ''
        }
      };

      const snapshot = await uploadBytes(storageRef, file, metadata);
      const url = await getDownloadURL(snapshot.ref);

      // Generar thumbnail si es una imagen
      let thumbnailUrl = url;
      if (file.type.startsWith('image/')) {
        thumbnailUrl = url; // En este caso usamos la misma URL
      } else {
        thumbnailUrl = ''; // Para documentos no imagen, no hay thumbnail
      }

      // Guardar referencia en Firestore
      const documentosRef = collection(db, 'documentos');
      const nuevoDocumento = {
        url,
        thumbnailUrl,
        nombre,
        descripcion: '',
        tipo: 'otro',
        mimeType: file.type,
        obra: selectedObraId, // Save 'obra' for query compatibility
        obraId: selectedObraId, // Save 'obraId' for type consistency
        fechaSubida: new Date(),
        usuarioId: usuario?.uid || '',
        usuarioEmail: usuario?.email || '',
      };

      await addDoc(documentosRef, nuevoDocumento);
      await cargarDocumentos();
      setOpenSubirDocumento(false);
    } catch (err: any) {
      setError(err.message || 'Error al subir el documento');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleBorrarDocumento = async (documento: Documento) => {
    setError(null);
    setLoading(true);
    try {
      if (!puedeEditar()) {
        throw new Error('No tienes permisos para borrar documentos');
      }
      if (!verificarPermisosObra(documento.obraId)) {
        throw new Error('No tienes permisos para borrar documentos de esta obra');
      }

      // Borrar el archivo de Storage
      const storageRef = ref(storage, documento.url);
      await deleteObject(storageRef);

      // Borrar el documento de Firestore
      const docRef = doc(db, 'documentos', documento.id);
      await deleteDoc(docRef);

      // Recargar los documentos
      await cargarDocumentos();
    } catch (err: any) {
      setError('Error al borrar el documento. Por favor, intente nuevamente.');
      console.error('Error al borrar documento:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
              alignSelf: { xs: 'flex-start', sm: 'center' }
            }}
          >
            Planos y Documentos
          </Typography>

          {!isMobile && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpenSubirDocumento(true)}
              disabled={obras.length === 0 || !puedeEditar()}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Subir Documento
            </Button>
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Buscar por nombre, descripción..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.default'
                  }
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
              Mostrando {documentos.length} documento{documentos.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Paper>

        {loadingDocumentos ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress size={40} thickness={4} />
          </Box>
        ) : documentos.length === 0 ? (
          <Fade in>
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                color: 'text.secondary',
                borderRadius: 4,
                bgcolor: 'background.default',
                border: '1px dashed',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                No se encontraron documentos
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: 400 }}>
                {obraSeleccionada
                  ? "No hay documentos para esta obra. Intenta subir uno nuevo."
                  : "Selecciona una obra o sube documentos para comenzar a organizar tus planos y archivos."}
              </Typography>
              {isMobile && (
                <Button
                  variant="outlined"
                  onClick={() => setOpenSubirDocumento(true)}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  Subir ahora
                </Button>
              )}
            </Paper>
          </Fade>
        ) : (
          <DocumentosGaleria
            documentos={documentos}
            obras={obras}
            onBorrarDocumento={handleBorrarDocumento}
            loading={loading}
          />
        )}

        <SubirDocumentoForm
          open={openSubirDocumento}
          onClose={() => setOpenSubirDocumento(false)}
          onSubmit={handleSubirDocumento}
          obras={obras}
          loading={loading}
          defaultObraId={obraSeleccionada}
        />

        {isMobile && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => setOpenSubirDocumento(true)}
            disabled={obras.length === 0 || !puedeEditar()}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              boxShadow: 4
            }}
          >
            <AddIcon />
          </Fab>
        )}
      </Container>
    </Layout>
  );
};

export default DocumentosPage;